"use client"

import { useEffect } from 'react'

/**
 * CacheVersionChecker component
 * 
 * Detects JavaScript cache version mismatches and automatically reloads the page
 * when chunk loading errors occur. This is especially important after deployments
 * when users may have cached old JavaScript files.
 * 
 * Common scenarios:
 * - User has old JS cached, server has new build → chunk loading fails → auto-reload
 * - Network issues loading JS chunks → detected and handled gracefully
 * - Timeout errors when loading chunks → auto-reload
 */
export function CacheVersionChecker() {
  useEffect(() => {
    // Track if we've already reloaded to prevent reload loops
    const RELOAD_KEY = 'cache_version_reload_attempted'
    
    // Clear reload flag on successful page load (after a delay to ensure page is stable)
    const clearReloadFlag = () => {
      // Wait a bit to ensure the page has fully loaded
      setTimeout(() => {
        sessionStorage.removeItem(RELOAD_KEY)
      }, 2000)
    }
    
    // Clear flag if page loaded successfully
    if (document.readyState === 'complete') {
      clearReloadFlag()
    } else {
      window.addEventListener('load', clearReloadFlag, { once: true })
    }
    
    const hasReloaded = sessionStorage.getItem(RELOAD_KEY)
    
    // Enhanced function to detect chunk loading / bundle mismatch errors
    const isChunkLoadingError = (error: any, errorString?: string): boolean => {
      const str = errorString || String(error || '')
      const errorMessage = error?.message || str
      const errorName = error?.name || ''
      
      // Webpack/Next.js runtime: module is undefined when executing (stale cache or chunk mismatch)
      const isWebpackModuleError =
        (errorMessage?.includes("reading 'call'") || str.includes("reading 'call'")) &&
        (errorMessage?.includes('undefined') || str.includes('undefined'))
      
      // Check for chunk loading error patterns
      return (
        isWebpackModuleError ||
        errorName === 'ChunkLoadError' ||
        str.includes('ChunkLoadError') ||
        str.includes('Loading chunk') ||
        str.includes('Failed to fetch dynamically imported module') ||
        str.includes('_next/static') ||
        str.includes('chunk') && (str.includes('failed') || str.includes('timeout') || str.includes('error')) ||
        errorMessage?.includes('_next/static') ||
        errorMessage?.includes('chunk') && (errorMessage.includes('failed') || errorMessage.includes('timeout'))
      )
    }
    
    // Check for chunk loading errors (indicates version mismatch)
    const handleChunkLoadError = (event: ErrorEvent) => {
      const error = event.error || event.message
      const errorString = String(error)
      
      // Enhanced detection (include main.js / eval - webpack runtime module errors)
      const isChunkError = isChunkLoadingError(error, errorString) ||
        (event.filename && (event.filename.includes('_next/static') || event.filename.includes('main.js')))
      
      if (isChunkError && !hasReloaded) {
        console.warn(' [CACHE-VERSION] Detected chunk loading error (likely cache mismatch):', {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: error
        })
        
        // Mark that we've attempted a reload
        sessionStorage.setItem(RELOAD_KEY, 'true')
        
        // Reload the page to fetch fresh JS files
        console.log(' [CACHE-VERSION] Reloading page to fetch fresh JavaScript files...')
        window.location.reload()
      }
    }
    
    // Handle unhandled promise rejections (chunk loading often fails as promises)
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      const reasonString = String(reason)
      
      // Enhanced detection for promise rejections
      const isChunkError = isChunkLoadingError(reason, reasonString) ||
        (reason instanceof Error && reason.message.includes('_next/static'))
      
      if (isChunkError && !hasReloaded) {
        console.warn(' [CACHE-VERSION] Detected chunk loading error in promise rejection:', {
          reason: reason,
          message: reason instanceof Error ? reason.message : reasonString
        })
        
        // Mark that we've attempted a reload
        sessionStorage.setItem(RELOAD_KEY, 'true')
        
        // Reload the page to fetch fresh JS files
        console.log(' [CACHE-VERSION] Reloading page to fetch fresh JavaScript files...')
        window.location.reload()
      }
    }
    
    // Also intercept fetch errors for chunk files
    const originalFetch = window.fetch
    window.fetch = function(...args) {
      return originalFetch.apply(this, args).catch((error) => {
        const url = args[0]?.toString() || ''
        
        // Check if this is a chunk file fetch that failed
        if (url.includes('_next/static') && url.includes('chunk')) {
          const isChunkError = isChunkLoadingError(error) || 
            error.message?.includes('timeout') ||
            error.message?.includes('Failed to fetch')
          
          if (isChunkError && !sessionStorage.getItem(RELOAD_KEY)) {
            console.warn(' [CACHE-VERSION] Detected chunk fetch error:', {
              url: url,
              error: error.message
            })
            
            sessionStorage.setItem(RELOAD_KEY, 'true')
            console.log(' [CACHE-VERSION] Reloading page to fetch fresh JavaScript files...')
            window.location.reload()
            return Promise.reject(error)
          }
        }
        
        return Promise.reject(error)
      })
    }
    
    // Listen for chunk loading errors
    window.addEventListener('error', handleChunkLoadError, true)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    
    // Cleanup
    return () => {
      window.removeEventListener('error', handleChunkLoadError, true)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      window.removeEventListener('load', clearReloadFlag)
      // Restore original fetch
      window.fetch = originalFetch
    }
  }, [])
  
  // This component doesn't render anything
  return null
}

